import { Position } from '../../domain/Position';
import { TerritoryState } from '../../domain/Territory';
/**
 * TerritoryGroupAnalyzer - Analyzes connected territory groups
 *
 * Groups are territories of the same owner that are orthogonally connected.
 * A group is surrounded only when ALL its external borders are occupied by enemies.
 */
export class TerritoryGroupAnalyzer {
    board;
    constructor(board) {
        this.board = board;
    }
    /**
     * Finds the connected group that includes the given position
     */
    findGroup(position) {
        const territory = this.board.getTerritoryAt(position);
        if (!territory || territory.owner === null) {
            return null;
        }
        const owner = territory.owner;
        const visited = new Set();
        const groupPositions = [];
        const queue = [position];
        // Flood fill to find all connected territories of same owner
        while (queue.length > 0) {
            const current = queue.shift();
            const key = current.toKey();
            if (visited.has(key))
                continue;
            visited.add(key);
            const currentTerritory = this.board.getTerritoryAt(current);
            if (!currentTerritory || currentTerritory.owner !== owner) {
                continue;
            }
            groupPositions.push(current);
            // Add orthogonal neighbors to queue
            const neighbors = current.getOrthogonalNeighbors();
            for (const neighbor of neighbors) {
                if (!visited.has(neighbor.toKey())) {
                    queue.push(neighbor);
                }
            }
        }
        // Find perimeter (external borders)
        const perimeter = this.findGroupPerimeter(groupPositions, owner);
        // Check if group is surrounded
        const isSurrounded = this.isGroupSurrounded(perimeter, owner);
        return {
            owner,
            positions: groupPositions,
            perimeter,
            isSurrounded,
        };
    }
    /**
     * Finds all groups on the board
     */
    findAllGroups() {
        const groups = [];
        const visited = new Set();
        for (const [key, territory] of this.board.territories) {
            if (visited.has(key) || territory.owner === null) {
                continue;
            }
            const position = Position.fromKey(key);
            const group = this.findGroup(position);
            if (group) {
                // Mark all positions in group as visited
                for (const pos of group.positions) {
                    visited.add(pos.toKey());
                }
                groups.push(group);
            }
        }
        return groups;
    }
    /**
     * Finds the perimeter (external border) of a group
     */
    findGroupPerimeter(groupPositions, owner) {
        const perimeter = [];
        const groupSet = new Set(groupPositions.map(p => p.toKey()));
        for (const position of groupPositions) {
            const neighbors = position.getOrthogonalNeighbors();
            for (const neighbor of neighbors) {
                const neighborKey = neighbor.toKey();
                // If neighbor is not in the group, it's part of the perimeter
                if (!groupSet.has(neighborKey)) {
                    const territory = this.board.getTerritoryAt(neighbor);
                    // Include board edges and territories not owned by this group
                    if (!territory || territory.owner !== owner) {
                        // Avoid duplicates in perimeter
                        if (!perimeter.some(p => p.equals(neighbor))) {
                            perimeter.push(neighbor);
                        }
                    }
                }
            }
        }
        return perimeter;
    }
    /**
     * Checks if a group's perimeter is completely surrounded
     * По дизайн-документу (строка 516): "Территория захватывается, когда она полностью
     * окружена другими сторонами (не своего цвета)"
     */
    isGroupSurrounded(perimeter, groupOwner) {
        if (perimeter.length === 0)
            return false;
        for (const position of perimeter) {
            const territory = this.board.getTerritoryAt(position);
            // Board edge counts as surrounding
            if (!territory)
                continue;
            // Burned land acts as a wall (like board edge)
            if (territory.state === TerritoryState.BURNED)
                continue;
            // If any perimeter position is neutral (unoccupied), group is not surrounded
            if (territory.owner === null) {
                return false;
            }
            // If territory in perimeter belongs to the SAME side - NOT surrounded
            if (territory.owner === groupOwner) {
                return false;
            }
        }
        // All perimeter positions are occupied by OTHER sides, burned, or board edges
        return true;
    }
    /**
     * Read-only: groups that are ONE move away from being fully surrounded — exactly
     * one neutral perimeter cell remains, everything else blocks (enemy/burned/edge),
     * and there is no same-owner escape. Drives the tutorial "semi-encirclement"
     * warning. Never mutates the board (unlike wouldCompleteSurrounding). When
     * touchedKeys is given, only groups whose perimeter includes one of those cells
     * are returned, so we warn solely about groups the just-played move affected.
     */
    getSemiSurroundedGroups(touchedKeys) {
        const result = [];
        for (const group of this.findAllGroups()) {
            if (group.isSurrounded || group.owner === null)
                continue;
            let openCount = 0;
            let friendlyCount = 0;
            let openPosition = null;
            const surrounding = new Set();
            for (const pos of group.perimeter) {
                const territory = this.board.getTerritoryAt(pos);
                if (!territory)
                    continue; // board edge = wall
                if (territory.state === TerritoryState.BURNED)
                    continue; // burned = wall
                if (territory.owner === null) {
                    openCount++;
                    openPosition = pos;
                    continue;
                }
                if (territory.owner === group.owner) {
                    friendlyCount++;
                    continue;
                }
                surrounding.add(territory.owner);
            }
            if (friendlyCount === 0 && openCount === 1 && openPosition && surrounding.size >= 1) {
                if (touchedKeys && !group.perimeter.some(p => touchedKeys.has(p.toKey())))
                    continue;
                result.push({
                    owner: group.owner,
                    positions: group.positions,
                    openPosition,
                    surroundingSides: [...surrounding],
                });
            }
        }
        return result;
    }
    /**
     * Checks if a group is surrounded by a specific side
     */
    isGroupSurroundedBy(group, bySide) {
        if (!group.isSurrounded)
            return false;
        // Check if all perimeter positions are occupied by the specified side
        // (or board edges)
        for (const position of group.perimeter) {
            const territory = this.board.getTerritoryAt(position);
            // Board edge is OK
            if (!territory)
                continue;
            // Must be owned by the surrounding side
            if (territory.owner !== bySide) {
                // Multiple sides involved in surrounding
                return false;
            }
        }
        return true;
    }
    /**
     * Gets all groups that are surrounded
     */
    getSurroundedGroups() {
        return this.findAllGroups().filter(g => g.isSurrounded);
    }
    /**
     * Checks if capturing a position would complete the surrounding of any group
     */
    wouldCompleteSurrounding(position, capturingSide) {
        const completedGroups = [];
        // Temporarily set the position as captured
        const originalTerritory = this.board.getTerritoryAt(position);
        if (!originalTerritory)
            return completedGroups;
        // Снимок ВСЕГО состояния: откат через capture()/makeNeutral() переписывал
        // previousOwner и сбрасывал wasEnemyActiveField, стирая начисленные 30 очков.
        const snapshot = originalTerritory.snapshot();
        originalTerritory.capture(capturingSide);
        // Check all neighboring positions for groups
        const neighbors = position.getOrthogonalNeighbors();
        const checkedGroups = new Set();
        for (const neighbor of neighbors) {
            const territory = this.board.getTerritoryAt(neighbor);
            if (!territory || territory.owner === null || territory.owner === capturingSide) {
                continue;
            }
            // Find the group this neighbor belongs to
            const group = this.findGroup(neighbor);
            if (!group)
                continue;
            // Create a unique key for this group to avoid checking it multiple times
            const groupKey = `${group.owner}:${group.positions
                .map(p => p.toKey())
                .sort()
                .join(',')}`;
            if (checkedGroups.has(groupKey))
                continue;
            checkedGroups.add(groupKey);
            // Check if this group would be surrounded after the capture
            if (group.isSurrounded) {
                completedGroups.push(group);
            }
        }
        // Restore original state verbatim — симуляция не должна оставлять следов.
        originalTerritory.restoreSnapshot(snapshot);
        return completedGroups;
    }
}
//# sourceMappingURL=TerritoryGroupAnalyzer.js.map